import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * <SEO /> - per-route head manager.
 *
 * Wraps react-helmet-async with the conventions we use across the site:
 * absolute canonical/OG URLs, a single source of truth for the default
 * OG image, sensible Twitter Card fallbacks, and an optional `noIndex`
 * flag for private surfaces (Settings, /chat, /join).
 *
 * Why this exists:
 * The root index.html provides the LANDING-PAGE defaults that crawlers
 * see if JS doesn't run. For every other route we want to override
 * those at runtime so the title bar, OG preview, Twitter card, and any
 * route-specific JSON-LD all match the page that's actually rendered.
 * Future /compare/* and /use-cases/* slugs can drop this in without
 * touching index.html.
 *
 * Usage:
 *
 *   // Landing page (matches index.html defaults - included for clarity)
 *   <SEO />
 *
 *   // Blog post
 *   <SEO
 *     title="Why Your AI Slide Tool Ignores Your Brand Guidelines"
 *     description="…155-char hook…"
 *     canonical="/blog/brand-guidelines"
 *     ogImage="/blog/brand-guidelines.jpg"
 *     ogType="article"
 *     schema={articleSchema}        // optional JSON-LD object(s)
 *   />
 *
 *   // Private route - keep out of the index
 *   <SEO title="Settings" noIndex />
 *
 *   // Future comparison page
 *   <SEO
 *     title="Restack vs Rogo | AI for consultants"
 *     description="…"
 *     canonical="/compare/restack-vs-rogo"
 *   />
 */

const SITE_URL = 'https://re-stack.com';
const SITE_NAME = 'Restack';
const DEFAULT_TITLE = 'Restack | The deliverable engine for Private Equity teams';
const DEFAULT_DESCRIPTION =
  'AI for Private Equity teams that builds the deck, model, and memo in your live Office files, on your firm templates, with every figure cited.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_OG_IMAGE_ALT =
  'Restack AI editor shown inside PowerPoint with the chat sidebar open';

/** Resolve a possibly-relative path to a fully-qualified absolute URL. */
function absolutize(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith('/')) return `${SITE_URL}${pathOrUrl}`;
  return `${SITE_URL}/${pathOrUrl}`;
}

/**
 * Pick the per-page title format. We append " | Restack" to non-default
 * titles so SERP entries are clearly branded; titles already containing
 * "Restack" are left alone so we don't double-stamp. Pipe separator
 * (instead of em-dash) keeps tab titles clean and consistent across the
 * site - em-dashes were creating a slightly editorial register in the
 * browser tab that didn't match the institutional brand voice.
 */
function buildTitle(rawTitle) {
  if (!rawTitle) return DEFAULT_TITLE;
  if (/restack/i.test(rawTitle)) return rawTitle;
  return `${rawTitle} | ${SITE_NAME}`;
}

/**
 * Derive a short breadcrumb label from a page title: take the part before the
 * first " | " or " - " separator and drop a trailing " | Restack". So
 * "Restack vs Claude - AI Decks for ..." becomes "Restack vs Claude" and
 * "Pricing | Restack" becomes "Pricing".
 */
function labelFromTitle(rawTitle) {
  if (!rawTitle) return null;
  return rawTitle
    .split(/\s+[|-]\s+/)[0]
    .replace(/\s*\|\s*Restack\s*$/i, '')
    .trim();
}

/**
 * Two-level BreadcrumbList (Home > current page). Google uses this to render
 * the breadcrumb trail under the result and to understand site hierarchy,
 * which also feeds sitelink generation. Skipped for the home page and any
 * noindex route.
 */
function buildBreadcrumb(itemUrl, name) {
  if (!name) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name, item: itemUrl },
    ],
  };
}

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogType = 'website',
  noIndex = false,
  // Free-form JSON-LD: pass an object or an array of objects to inject
  // page-specific structured data (Article, Product, FAQPage, etc.) on
  // top of the site-wide Organization + SoftwareApplication declared
  // in public/index.html.
  schema = null,
  // Optional override - useful for /blog/:slug where we want to set
  // article-specific og:type and timestamps.
  articleMeta = null,
  // Optional clean breadcrumb label. Defaults to a label derived from the
  // page title; pass this when the title is long or awkward as a crumb.
  breadcrumbLabel = null,
}) {
  const resolvedTitle = buildTitle(title);
  const resolvedCanonical = absolutize(canonical) || `${SITE_URL}/`;
  const resolvedOgImage = absolutize(ogImage) || DEFAULT_OG_IMAGE;
  const robots = noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1';

  // Auto breadcrumb (Home > this page) for every indexable non-home route, so
  // each page declares its place in the hierarchy without per-page wiring.
  const breadcrumb =
    !noIndex && resolvedCanonical !== `${SITE_URL}/`
      ? buildBreadcrumb(resolvedCanonical, breadcrumbLabel || labelFromTitle(title))
      : null;

  // Normalise schema into a stable array, then append the breadcrumb.
  const schemaList = [
    ...(!schema ? [] : Array.isArray(schema) ? schema : [schema]),
    ...(breadcrumb ? [breadcrumb] : []),
  ];

  // Per-page JSON-LD is injected straight into <head> rather than through
  // Helmet. Helmet (v2) silently drops <script> children, so every page's
  // structured data was being built and then thrown away: the Article block
  // on the comparison pages, the Product block on pricing, the FAQPage
  // blocks. Nothing shipped. Managing the tag directly makes it observable
  // and removes it cleanly when the route changes.
  const schemaJson = schemaList.length
    ? JSON.stringify(schemaList.length === 1 ? schemaList[0] : schemaList)
    : null;
  useEffect(() => {
    if (!schemaJson || typeof document === 'undefined') return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seoSchema = 'route';
    el.textContent = schemaJson;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [schemaJson]);

  return (
    <Helmet>
      {/* Primary */}
      <title>{resolvedTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={resolvedCanonical} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:alt" content={ogImageAlt} />

      {/* Article-specific OG (only when ogType === 'article') */}
      {articleMeta?.publishedTime && (
        <meta property="article:published_time" content={articleMeta.publishedTime} />
      )}
      {articleMeta?.modifiedTime && (
        <meta property="article:modified_time" content={articleMeta.modifiedTime} />
      )}
      {articleMeta?.section && (
        <meta property="article:section" content={articleMeta.section} />
      )}
      {Array.isArray(articleMeta?.tags) &&
        articleMeta.tags.map((tag) => (
          <meta property="article:tag" content={tag} key={`tag-${tag}`} />
        ))}

      {/* Twitter - falls back to OG when fields absent, but explicit is safer */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@restackHQ" />
      <meta name="twitter:creator" content="@restackHQ" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOgImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

    </Helmet>
  );
}
